import { DEMO_CURRENCY } from 'helpers/constants';
import type { CurrencyCode } from 'helpers/currency';
import { useCollections } from 'modules/common/hooks/useCollections';
import { useThemes } from 'modules/common/hooks/useThemes';
import type { FC } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import type {
  Game,
  GameCategory,
  GamesFilter,
  GameV2,
  Provider,
} from '../../api/games';
import {
  deleteFavoriteGame as fetchDeleteFavoriteGame,
  favoriteGames as fetchFavoriteGames,
  favoriteGamesIds as fetchFavoriteGamesIds,
  gamesFilterV2 as fetchGamesFilter,
  gamesFilterSelect as fetchGamesFilterSelect,
  providers as fetchProviders,
  recentGames as fetchRecentGames,
  setFavoriteGame as fetchSetFavoriteGame,
} from '../../api/games';
import type { Winner } from '../../api/winners';
import { latestWinners } from '../../api/winners';
import { isMobileDevice } from '../../helpers/common';
import { getGamePath, mapGameV2ToGame } from '../../helpers/game';
import { useApi } from '../../hooks/api';
import { AuthContext } from '../auth';

export interface GamesContextData {
  categories: Map<string, GameCategory>;
  themes: Map<string, GameCategory>;
  categoriesByRegEx: (regEx: RegExp) => Map<string, GameCategory>;
  pendingCategories: boolean;
  providers: Provider[];
  pendingProviders: boolean;
  favoriteGames: Game[];
  favoriteGamesIds: number[];
  recentGames: Game[];
  winners: Winner[];
  pendingWinners: boolean;
  pendingFavoriteGames: boolean;
  pendingRecentGames: boolean;
  fetchGamesV2: (
    body?: Omit<GamesFilter.Body, 'currencies'>
  ) => Promise<GamesFilter.ResponseV2 | undefined>;
  getRecentGames: () => Promise<void>;
}

export interface GamesContextAction {
  addFavoriteGames: (currencyCode: Game['real']) => Promise<void>;
  removeFavoriteGames: (currencyCode: Game['real']) => Promise<void>;
}

export const GamesContext = createContext<
  GamesContextData & GamesContextAction
>(null as any);

export const GamesContextProvider: FC = ({ children }) => {
  const { isAuthenticated, user, wallets, pendingUser } =
    useContext(AuthContext);

  const [favoriteGamesIds, setFavoriteGamesIds] = useState<number[]>([]);
  const [favoriteGames, setFavoriteGames] = useState<Game[]>([]);
  const [recentGames, setRecentGames] = useState<Game[]>([]);
  const [pendingFavoriteGames, setPendingFavoriteGames] = useState(true);
  const [pendingRecentGames, setPendingRecentGames] = useState(true);

  const { collections, pending: pendingCategories } = useCollections();
  const { themes } = useThemes();

  const [
    requestProviders,
    { data: providers = [], pending: pendingProviders },
  ] = useApi(fetchProviders);

  const [requestWinners, { data: dataWinners, pending: pendingWinners }] =
    useApi(latestWinners);

  useEffect(() => {
    requestProviders();
    requestWinners();
  }, [requestProviders, requestWinners]);

  const fetchFavoriteGame = useCallback(async () => {
    try {
      const { data: favoriteGames } = await fetchFavoriteGames();
      const { data: favoriteGamesIds } = await fetchFavoriteGamesIds();

      if (favoriteGamesIds && favoriteGames) {
        const favoriteGamesUpdated = mapGameV2ToGame(favoriteGames);
        setFavoriteGamesIds(favoriteGamesIds);
        setFavoriteGames(favoriteGamesUpdated);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setPendingFavoriteGames(false);
    }
  }, []);

  const categories = useMemo(() => {
    if (!collections) return new Map();

    const result = new Map<string, GameCategory>();
    collections.forEach((category) => {
      if (
        /^_top_/.test(category.id) ||
        (!/^_/.test(category.id) && /^[a-z]/.test(category.id))
      ) {
        result.set(category.id, category);
      }
    });
    return result;
  }, [collections]);

  const categoriesByRegEx = useCallback(
    (regEx: RegExp) => {
      if (!collections) return new Map();

      const result = new Map<string, GameCategory>();
      collections.forEach((category) => {
        if (regEx.test(category.id)) {
          result.set(category.id, category);
        }
      });
      return result;
    },
    [collections]
  );

  const winners = useMemo(() => {
    if (!dataWinners) return [];

    return dataWinners.map((winner) => ({
      ...winner,
      path: getGamePath(winner.gameTitle),
    }));
  }, [dataWinners]);

  const addFavoriteGames = useCallback(
    async (currencies: Game['real']) => {
      if (!(user?.currency && currencies)) return;

      const currencyCode = currencies[user.currency].id;

      try {
        await fetchSetFavoriteGame(currencyCode);
        await fetchFavoriteGame();
      } catch (error) {
        console.error(error);
      }
    },
    [user, fetchFavoriteGame]
  );

  const removeFavoriteGames = useCallback(
    async (currencies: Game['real']) => {
      if (!currencies) return;

      const favoriteGameId = Object.values(currencies)
        .map(({ id }) => id)
        .find((c) => favoriteGamesIds.includes(c));

      if (!favoriteGameId) return;

      try {
        await fetchDeleteFavoriteGame(favoriteGameId);
        await fetchFavoriteGame();
      } catch (error) {
        console.error(error);
      }
    },
    [favoriteGamesIds, fetchFavoriteGame]
  );

  const accounts = useMemo(
    () => wallets.map(({ currency }) => currency) as CurrencyCode[],
    [wallets]
  );

  const fetchGamesV2 = useCallback(
    async (
      body?: Omit<GamesFilter.Body, 'currencies'>
    ): Promise<GamesFilter.ResponseV2 | undefined> => {
      try {
        const { data } = await fetchGamesFilter({
          device: isMobileDevice ? 'mobile' : 'desktop',
          currencies: isAuthenticated ? accounts : [DEMO_CURRENCY],
          ...body,
        });

        if (data) {
          const { data: games, pagination } = data;
          return {
            data: mapGameV2ToGame(games),
            pagination,
          };
        }
      } catch (error) {
        console.error(error);
        throw error;
      }
    },
    [isAuthenticated, accounts]
  );

  const fetchGamesSelectV2 = useCallback(
    async (
      body: GamesFilter.BodySelect
    ): Promise<GamesFilter.ResponseSelect | undefined> => {
      try {
        const res = await fetchGamesFilterSelect({
          device: isMobileDevice ? 'mobile' : 'desktop',
          ...body,
        });

        return res.data;
      } catch (error) {
        console.error(error);
      }
    },
    []
  );

  const getRecentGames = useCallback(async () => {
    try {
      const response = await fetchRecentGames();
      if (!response) return;
      const gameIds = response.data.map(({ identifier }) => identifier);
      const responseGames = await fetchGamesSelectV2({ gameIds });

      if (!responseGames) return;

      const recentGameValues = Object.values(responseGames);

      const orderedGames = gameIds
        .map((id) => recentGameValues.find((game) => game.identifier === id))
        .filter((g): g is GameV2.Game => Boolean(g));

      setRecentGames(mapGameV2ToGame(orderedGames));
    } catch (error) {
      console.error(error);
    } finally {
      setPendingRecentGames(false);
    }
  }, [fetchGamesSelectV2]);

  useEffect(() => {
    if (pendingUser) return;

    if (isAuthenticated) {
      fetchFavoriteGame();
      getRecentGames();
    } else {
      setFavoriteGamesIds([]);
      setFavoriteGames([]);
      setRecentGames([]);
      setPendingFavoriteGames(false);
      setPendingRecentGames(false);
    }
  }, [fetchFavoriteGame, getRecentGames, isAuthenticated, pendingUser]);

  const contextValue = useMemo(
    () => ({
      categories,
      themes,
      categoriesByRegEx,
      pendingCategories,
      providers,
      pendingProviders,
      favoriteGames,
      favoriteGamesIds,
      addFavoriteGames,
      removeFavoriteGames,
      recentGames,
      winners,
      pendingWinners,
      pendingFavoriteGames,
      pendingRecentGames,
      fetchGamesV2,
      getRecentGames,
    }),
    [
      addFavoriteGames,
      categories,
      categoriesByRegEx,
      favoriteGames,
      favoriteGamesIds,
      fetchGamesV2,
      getRecentGames,
      pendingCategories,
      pendingFavoriteGames,
      pendingProviders,
      pendingRecentGames,
      pendingWinners,
      providers,
      recentGames,
      removeFavoriteGames,
      themes,
      winners,
    ]
  );

  return (
    <GamesContext.Provider value={contextValue}>
      {children}
    </GamesContext.Provider>
  );
};
